import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { LeadExecutionEngine } from '../../api/LeadExecutionEngine';
import { FiRefreshCw, FiTarget } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { LeadKanbanColumn } from './LeadKanbanColumn';
import { LeadKanbanCard } from './LeadKanbanCard';
import LeadCard, { LEAD_STATUS_MAP } from '../../components/cards/LeadCard';

export default function LeadGestaoPage() {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeDragItem, setActiveDragItem] = useState(null);

    const loadData = async () => {
        setLoading(true);
        const res = await LeadExecutionEngine.listarAtivos();
        setLeads(res || []);
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    // Helper to categorize leads dynamically based on the 10 statuses
    const ignoredStatuses = ['NOVO', 'FECHADO_GANHO', 'FECHADO_PERDIDO', 'CONGELADO'];
    const columnsData = LEAD_STATUS_MAP
        .filter(s => !ignoredStatuses.includes(s.id))
        .map(s => ({
            id: `col-${s.id.toLowerCase()}`,
            statusTarget: s.id,
            title: s.label,
            colorClass: s.color.replace('bg-', 'text-') + ' border-' + s.color.replace('bg-', ''),
            leads: leads.filter(l => l.statusComercial === s.id)
        }));

    const handleStatusUpdate = async (id, novoStatus, comentario = 'Atualizado via Kanban') => {
        setLoading(true);
        const ok = await LeadExecutionEngine.atualizarStatus(id, novoStatus, 'OPERADOR_LOGADO', comentario);
        if (ok) { 
            toast.success("LEAD REPOSICIONADO NO FUNIL"); 
            await loadData(); 
        } else {
            toast.error("FALHA AO ATUALIZAR");
            setLoading(false);
        }
    };

    const handleDragStart = (event) => {
        const { active } = event;
        const lead = leads.find(l => l._id === active.id);
        setActiveDragItem(lead);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveDragItem(null);

        if (!over) return;
        
        const leadId = active.id;
        const targetColId = over.id;
        const currentColId = active.data.current?.colId;

        if (targetColId === currentColId) return;

        const targetColumn = columnsData.find(c => c.id === targetColId);
        if (targetColumn) {
            await handleStatusUpdate(leadId, targetColumn.statusTarget, `Movido para ${targetColumn.title}`);
        }
    };

    return (
        <MainLayout>
            <div className="flex flex-col h-[90vh]">
                <div className="flex justify-between items-end mb-6">
                    <div>
                        <h1 className="text-4xl font-black uppercase tracking-tighter flex items-center gap-4 text-white">
                            <FiTarget className="text-orange-500" />
                            Esteira de Leads
                        </h1>
                        <p className="text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-[0.2em] mt-1">
                            Funil de Vendas e Negociações (CRM)
                        </p>
                    </div>

                    <div className="flex gap-4">
                        <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-[10px] font-black uppercase transition-all text-white border border-white/10">
                            <FiRefreshCw className={loading ? "animate-spin" : ""} /> ATUALIZAR
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-x-auto pb-4 no-scrollbar cursor-grab active:cursor-grabbing">
                    <DndContext 
                        collisionDetection={closestCorners}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="flex gap-6 min-w-max h-full p-1 items-start">
                            {columnsData.map(col => (
                                <LeadKanbanColumn 
                                    key={col.id}
                                    id={col.id}
                                    title={col.title}
                                    leads={col.leads}
                                    colorClass={col.colorClass}
                                    onUpdate={handleStatusUpdate}
                                />
                            ))}
                        </div>

                        {/* FANTASMA DO ARRASTE */}
                        <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
                            {activeDragItem ? (
                                <div className="opacity-90 scale-105 shadow-2xl rotate-2">
                                    <LeadCard lead={activeDragItem} readOnly={true} />
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </div>
            </div>
        </MainLayout>
    );
}
